import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { AuthProvider } from './lib/auth-context.tsx';
import { ActivePracticeProvider } from './lib/active-practice.tsx';
import App from './App.tsx';
import { AppErrorBoundary } from './components/AppErrorBoundary.tsx';
import { scrubBrowserEvent, scrubBrowserBreadcrumb } from './lib/sentry-scrub.ts';
import { installChunkReloadRecovery } from './lib/chunk-reload-recovery.ts';
import { registerServiceWorker } from './pwa/register-sw.ts';
import { suppressInstallPrompt } from './pwa/suppress-install-prompt.ts';
import './lib/globals.css';

// Sentry must be initialised before any React rendering begins.
// No session replay - clinical data is Article 9 special category, and Sentry's DPA
// disapplies its Art. 28 obligations to any that reaches them.
Sentry.init({
  dsn: (import.meta.env['VITE_SENTRY_DSN'] as string | undefined) ?? '',
  enabled: !!import.meta.env['VITE_SENTRY_DSN'],
  // Deployment label for the SAME shared DSN/project across boxes (prod vs testbeta) —
  // deliberately NOT VITE_APP_ENV, which is unrelated application-environment semantics.
  // Falls back to 'production', the value every build has always effectively sent (Dockerfile
  // hardcodes VITE_APP_ENV=production for every build; this var was never wired to it), so a
  // box that hasn't set VITE_SENTRY_ENVIRONMENT yet keeps reporting what it always did.
  environment: (import.meta.env['VITE_SENTRY_ENVIRONMENT'] as string | undefined) || 'production',
  // No performance tracing via Sentry; backend OTel covers server-side traces.
  tracesSampleRate: 0,
  // GDPR: never attach user IP, email, or cookies to Sentry error events.
  sendDefaultPii: false,
  beforeSend: (event) => scrubBrowserEvent(event),
  beforeBreadcrumb: (crumb) => scrubBrowserBreadcrumb(crumb),
});

// Before any lazy() route chunk gets imported, so a stale tab's first navigation is covered.
installChunkReloadRecovery();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: (failureCount, error) => {
        if (error instanceof Error && 'status' in error) {
          const status = (error as { status: number }).status;
          if (status === 401 || status === 403 || status === 404) return false;
        }
        return failureCount < 2;
      },
    },
  },
});

const root = document.getElementById('root');
if (!root) throw new Error('Root element not found');

createRoot(root).render(
  <StrictMode>
    <AppErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <BrowserRouter>
            {/* Inside the router: the provider reads /me through useTokenStore,
                which calls useNavigate to bounce a 401 — outside a Router that
                throws on every page. Inside AuthProvider, which keys the choice
                by user. NOT in ProtectedLayout, which early-returns for the
                policy gates and the session shell and so would leave screens
                uncovered. */}
            <ActivePracticeProvider>
              <App />
            </ActivePracticeProvider>
          </BrowserRouter>
        </AuthProvider>
      </QueryClientProvider>
    </AppErrorBoundary>
  </StrictMode>,
);

registerServiceWorker();
suppressInstallPrompt();
